/*
  Freinage des demandes d'authentification cote navigateur.

  Ce module ne remplace pas les limites de Supabase Auth (par IP, cote
  serveur) : un script qui appelle l'API directement ne passe pas par ici. Il
  sert a ne pas laisser un utilisateur, ou un script naif pilotant le
  formulaire, enchainer les tentatives erronees, et a afficher un message clair
  plutot qu'un « mot de passe incorrect » trompeur quand Supabase refuse pour
  cause de volume.

  L'etat vit dans localStorage pour survivre a un rechargement de page. Toute
  lecture ou ecriture est protegee : navigation privee, stockage bloque ou
  environnement sans `window` retombent sur une memoire locale au module.
*/

export interface ThrottleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface ThrottleEntry {
  failures: number;
  lockedUntil: number;
  lastFailureAt: number;
}

type ThrottleState = Record<string, ThrottleEntry>;

export interface FailurePolicy {
  /** Nombre d'echecs toleres avant la premiere temporisation. */
  threshold: number;
  /** Temporisations successives, en ms ; la derniere sert de plafond. */
  delaysMs: number[];
}

const STORAGE_KEY = 'eclosion.auth.throttle.v1';

/** Un compteur sans nouvel echec depuis ce delai est oublie. */
export const FAILURE_WINDOW_MS = 15 * 60 * 1000;

const DELAYS_MS = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000];

/** Par adresse e-mail : 3 echecs, puis 5 s, 15 s, 30 s, 1 min, 2 min, 5 min. */
export const LOGIN_EMAIL_POLICY: FailurePolicy = { threshold: 3, delaysMs: DELAYS_MS };

/**
  Pour le navigateur, toutes adresses confondues : plus tolerant, pour ne pas
  bloquer quelqu'un qui hesite entre deux adresses, mais empeche de contourner
  le compteur par adresse en changeant d'e-mail a chaque essai.
*/
export const LOGIN_BROWSER_POLICY: FailurePolicy = { threshold: 10, delaysMs: DELAYS_MS };

/** Delai minimal entre deux demandes d'e-mail (reinitialisation, inscription). */
export const EMAIL_REQUEST_COOLDOWN_MS = 60_000;

export const loginEmailKey = (email: string) => `login:${email.trim().toLowerCase()}`;
export const LOGIN_BROWSER_KEY = 'login:*';
export const RESET_PASSWORD_KEY = 'reset-password';
export const SIGN_UP_KEY = 'sign-up';

const memoryFallback = new Map<string, string>();

const safeStorage: ThrottleStorage = {
  getItem(key) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem(key);
      }
    } catch {
      // stockage indisponible : on retombe sur la memoire
    }
    return memoryFallback.get(key) ?? null;
  },
  setItem(key, value) {
    memoryFallback.set(key, value);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, value);
      }
    } catch {
      // stockage indisponible : la memoire suffit pour la session en cours
    }
  },
};

export interface ThrottleOptions {
  storage?: ThrottleStorage;
  now?: number;
}

function readState(storage: ThrottleStorage): ThrottleState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ThrottleState) : {};
  } catch {
    return {};
  }
}

function writeState(storage: ThrottleStorage, state: ThrottleState) {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // rien a faire : au pire, le freinage ne survit pas au rechargement
  }
}

/** Retire les entrees dont le verrou est echu et le compteur perime. */
function prune(state: ThrottleState, now: number): ThrottleState {
  const next: ThrottleState = {};
  for (const [key, entry] of Object.entries(state)) {
    const stale = now - entry.lastFailureAt > FAILURE_WINDOW_MS;
    if (entry.lockedUntil > now || !stale) next[key] = entry;
  }
  return next;
}

/** Temps restant avant de pouvoir retenter, en ms (0 si rien ne bloque). */
export function getLockRemainingMs(keys: string[], options: ThrottleOptions = {}): number {
  const storage = options.storage ?? safeStorage;
  const now = options.now ?? Date.now();
  const state = readState(storage);
  let remaining = 0;
  for (const key of keys) {
    const entry = state[key];
    if (entry) remaining = Math.max(remaining, entry.lockedUntil - now);
  }
  return Math.max(0, remaining);
}

/** Nombre d'echecs recents pour une cle (0 si le compteur est perime). */
export function getFailureCount(key: string, options: ThrottleOptions = {}): number {
  const storage = options.storage ?? safeStorage;
  const now = options.now ?? Date.now();
  const entry = readState(storage)[key];
  if (!entry || now - entry.lastFailureAt > FAILURE_WINDOW_MS) return 0;
  return entry.failures;
}

/**
  Enregistre un echec et pose la temporisation correspondante.
  Renvoie la duree du verrou pose, en ms (0 tant que le seuil n'est pas atteint).
*/
export function recordFailure(
  key: string,
  policy: FailurePolicy,
  options: ThrottleOptions = {},
): number {
  const storage = options.storage ?? safeStorage;
  const now = options.now ?? Date.now();
  const state = prune(readState(storage), now);
  const previous = state[key];
  const failures = (previous ? previous.failures : 0) + 1;
  const step = failures - policy.threshold;
  const delay =
    step > 0 ? policy.delaysMs[Math.min(step - 1, policy.delaysMs.length - 1)] : 0;
  state[key] = {
    failures,
    lockedUntil: Math.max(previous?.lockedUntil ?? 0, now + delay),
    lastFailureAt: now,
  };
  writeState(storage, state);
  return delay;
}

/** Pose un delai d'attente fixe (demande d'e-mail, refus 429 du serveur). */
export function startCooldown(key: string, durationMs: number, options: ThrottleOptions = {}) {
  const storage = options.storage ?? safeStorage;
  const now = options.now ?? Date.now();
  const state = prune(readState(storage), now);
  const previous = state[key];
  state[key] = {
    failures: previous?.failures ?? 0,
    lockedUntil: Math.max(previous?.lockedUntil ?? 0, now + durationMs),
    lastFailureAt: previous?.lastFailureAt ?? now,
  };
  writeState(storage, state);
}

/** Efface le compteur d'une cle (connexion reussie). */
export function clearThrottle(key: string, options: ThrottleOptions = {}) {
  const storage = options.storage ?? safeStorage;
  const now = options.now ?? Date.now();
  const state = prune(readState(storage), now);
  delete state[key];
  writeState(storage, state);
}

export type AuthErrorKind =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'rate_limited'
  | 'network'
  | 'other';

/**
  Classe une erreur renvoyee par supabase-js. On lit `status`, `code` et `name`
  sans importer les classes d'erreur, pour rester testable sans client.
*/
export function classifyAuthError(error: unknown): AuthErrorKind {
  if (!error || typeof error !== 'object') return 'other';
  const { status, code, name, message } = error as {
    status?: number;
    code?: string;
    name?: string;
    message?: string;
  };
  const text = (message ?? '').toLowerCase();

  if (
    status === 429 ||
    code === 'over_request_rate_limit' ||
    code === 'over_email_send_rate_limit' ||
    text.includes('rate limit') ||
    text.includes('too many requests')
  ) {
    return 'rate_limited';
  }
  if (code === 'email_not_confirmed' || text.includes('email not confirmed')) {
    return 'email_not_confirmed';
  }
  if (code === 'invalid_credentials' || text.includes('invalid login credentials')) {
    return 'invalid_credentials';
  }
  if (
    name === 'AuthRetryableFetchError' ||
    text.includes('failed to fetch') ||
    text.includes('networkerror') ||
    text.includes('network request failed')
  ) {
    return 'network';
  }
  return 'other';
}

/** « 45 s », « 2 min », « 2 min 30 s » : pour les comptes a rebours. */
export function formatWait(ms: number): string {
  const total = Math.max(1, Math.ceil(ms / 1000));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
}

/** Delai pose quand Supabase lui-meme refuse pour cause de volume (429). */
export const SERVER_RATE_LIMIT_COOLDOWN_MS = 60_000;

const LOGIN_MESSAGES: Record<AuthErrorKind, string> = {
  invalid_credentials: 'Email ou mot de passe incorrect',
  email_not_confirmed:
    "Votre adresse email n'est pas encore confirmée. Cliquez sur le lien reçu par email (pensez à vérifier vos spams).",
  rate_limited:
    'Trop de tentatives de connexion. Patientez quelques minutes avant de réessayer.',
  network:
    'Impossible de joindre le serveur. Vérifiez votre connexion internet et réessayez.',
  other: 'Une erreur est survenue lors de la connexion',
};

export interface LoginOutcome {
  kind: AuthErrorKind | null;
  /** Message a afficher, null si la connexion a reussi. */
  message: string | null;
}

/**
  Met a jour les compteurs apres une tentative de connexion et renvoie le
  message a afficher. Seuls les identifiants refuses comptent comme echec : une
  panne reseau ou un compte non confirme ne doivent pas allonger l'attente.
  Le message reste le meme que l'adresse existe ou non.
*/
export function registerLoginAttempt(
  email: string,
  error: unknown,
  options: ThrottleOptions = {},
): LoginOutcome {
  const emailKey = loginEmailKey(email);
  if (!error) {
    clearThrottle(emailKey, options);
    clearThrottle(LOGIN_BROWSER_KEY, options);
    return { kind: null, message: null };
  }
  const kind = classifyAuthError(error);
  if (kind === 'invalid_credentials') {
    recordFailure(emailKey, LOGIN_EMAIL_POLICY, options);
    recordFailure(LOGIN_BROWSER_KEY, LOGIN_BROWSER_POLICY, options);
  } else if (kind === 'rate_limited') {
    startCooldown(LOGIN_BROWSER_KEY, SERVER_RATE_LIMIT_COOLDOWN_MS, options);
  }
  return { kind, message: LOGIN_MESSAGES[kind] };
}
